import { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import styles from './Navbar.module.css';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../hooks/useStore';
import logo from '../../assets/kalden-logo.png';
import FilesDialog from './FilesDialog';
import { Home, Building2, FileText, Clock, Users, FolderOpen, LogOut, Receipt } from 'lucide-react';

interface NavLink {
  id: number;
  text: string;
  path: string;
  roles?: string[];
  icon?: React.ReactNode;
}

const navLinks: NavLink[] = [
  { id: 1, text: 'Ana Sayfa', path: '/dashboard', icon: <Home size={20} /> },
  {
    id: 2,
    text: 'Hastane Yönetimi',
    path: '/hospital-management',
    roles: ['User', 'Admin'],
    icon: <Building2 size={20} />,
  },
  {
    id: 3,
    text: 'Hastane Dosyaları',
    path: '/hospital-files',
    roles: ['User', 'Admin'],
    icon: <FileText size={20} />,
  },
  {
    id: 4,
    text: 'Mesai Yönetimi',
    path: '/overtime-management',
    roles: ['User', 'Admin'],
    icon: <Clock size={20} />,
  },
  {
    id: 5,
    text: 'Harcamalar',
    path: '/expense-management',
    roles: ['User', 'Admin'],
    icon: <Receipt size={20} />,
  },
  {
    id: 6,
    text: 'Kullanıcı Yönetimi',
    path: '/user-management',
    roles: ['Admin'],
    icon: <Users size={20} />,
  },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(false);
  const [isDesktop, setIsDesktop] = useState<boolean>(window.innerWidth >= 1280);
  const location = useLocation();
  const navigate = useNavigate();
  const { authStore, popupStore } = useStore();
  const openFilesDialog = () => popupStore.setIsFilesDialogOpen(true);

  const handleLogout = () => {
    authStore.logout();
    navigate('/login');
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
    localStorage.setItem('sidebarCollapsed', JSON.stringify(!isSidebarCollapsed));
  };

  // Check screen size for desktop vs mobile/tablet
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1280);
    };

    // Load sidebar state from localStorage
    const savedState = localStorage.getItem('sidebarCollapsed');
    if (savedState !== null) {
      setIsSidebarCollapsed(JSON.parse(savedState));
    }

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Update html class based on sidebar state for desktop
  useEffect(() => {
    const htmlElement = document.documentElement;
    if (isDesktop) {
      htmlElement.classList.add('desktop-sidebar');
      if (isSidebarCollapsed) {
        htmlElement.classList.add('sidebar-collapsed');
        htmlElement.classList.remove('sidebar-expanded');
      } else {
        htmlElement.classList.add('sidebar-expanded');
        htmlElement.classList.remove('sidebar-collapsed');
      }
    } else {
      htmlElement.classList.remove('desktop-sidebar', 'sidebar-collapsed', 'sidebar-expanded');
    }

    return () => {
      htmlElement.classList.remove('desktop-sidebar', 'sidebar-collapsed', 'sidebar-expanded');
    };
  }, [isDesktop, isSidebarCollapsed]);

  const filteredNavLinks = navLinks.filter(link => {
    if (!link.roles) return true;
    return authStore.hasAnyRole(link.roles);
  });

  // Desktop Sidebar Layout
  if (isDesktop) {
    return (
      <aside className={`${styles.sidebar} ${isSidebarCollapsed ? styles.collapsed : ''}`}>
        <div className={styles.sidebarHeader}>
          <NavLink to="/dashboard" className={styles.logoLink}>
            <img src={logo} alt="Kalden Logo" className={styles.logoImg} />
          </NavLink>
          <button
            className={styles.sidebarToggle}
            onClick={toggleSidebar}
            aria-label="Toggle sidebar"
          >
            <span className={`${styles.hamburger}`}></span>
          </button>
        </div>

        <nav className={styles.sidebarNav}>
          <ul className={styles.sidebarMenu}>
            {filteredNavLinks.map(link => (
              <li key={link.id}>
                <NavLink
                  to={link.path}
                  className={({ isActive }) =>
                    `${styles.sidebarLink} ${isActive ? styles.activeLink : ''}`
                  }
                  title={isSidebarCollapsed ? link.text : undefined}
                >
                  {link.icon}
                  <span className={styles.sidebarLinkText}>{link.text}</span>
                </NavLink>
              </li>
            ))}
            <li>
              <button
                onClick={openFilesDialog}
                className={styles.sidebarButton}
                title={isSidebarCollapsed ? 'Dosyalar' : undefined}
              >
                <FolderOpen size={20} />
                <span className={styles.sidebarLinkText}>Dosyalar</span>
              </button>
            </li>
            <li>
              <button
                onClick={handleLogout}
                className={styles.sidebarButton}
                title={isSidebarCollapsed ? 'Çıkış Yap' : undefined}
              >
                <LogOut size={20} />
                <span className={styles.sidebarLinkText}>Çıkış Yap</span>
              </button>
            </li>
          </ul>
        </nav>

        {/* User info at bottom */}
        <div className={styles.sidebarUserInfo}>
          <span className={styles.sidebarUserName}>
            {isSidebarCollapsed
              ? ''
              : `Merhaba ${authStore.username || authStore.user?.username || ''}`}
          </span>
        </div>

        {/* Global Files Dialog */}
        <FilesDialog />
      </aside>
    );
  }

  // Mobile/Tablet Navbar Layout
  return (
    <nav className={styles.navbar} style={{ marginBottom: '70px' }}>
      <div className={styles.navContainer}>
        <div className={styles.logo}>
          <NavLink to="/dashboard" className={styles.logoLink}>
            <img src={logo} alt="Kalden Logo" className={styles.logoImg} />
          </NavLink>
        </div>

        <ul className={styles.navMenu}>
          {filteredNavLinks.map(link => (
            <li key={link.id}>
              <NavLink
                to={link.path}
                className={({ isActive }) =>
                  `${styles.navLink} ${isActive ? styles.activeLink : ''}`
                }
              >
                {link.text}
              </NavLink>
            </li>
          ))}
          <li>
            <button onClick={openFilesDialog} className={styles.logoutButton}>
              Dosyalar
            </button>
          </li>
          <li>
            <button onClick={handleLogout} className={styles.logoutButton}>
              Çıkış Yap
            </button>
          </li>
        </ul>

        <button
          className={styles.mobileMenuButton}
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle menu"
        >
          <span className={`${styles.hamburger} ${isOpen ? styles.active : ''}`}></span>
        </button>

        <div className={`${styles.mobileMenu} ${isOpen ? styles.active : ''}`}>
          {filteredNavLinks.map(link => (
            <NavLink
              key={link.id}
              to={link.path}
              className={({ isActive }) =>
                `${styles.mobileLink} ${isActive ? styles.activeLink : ''}`
              }
            >
              {link.text}
            </NavLink>
          ))}
          <button onClick={openFilesDialog} className={styles.mobileLogoutButton}>
            Dosyalar
          </button>
          <button onClick={handleLogout} className={styles.mobileLogoutButton}>
            Çıkış Yap
          </button>
        </div>
        {/* Global Files Dialog */}
        <FilesDialog />
      </div>
    </nav>
  );
};

export default observer(Navbar);
